import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Clock, Filter, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import PageHero from '@/components/common/PageHero';
import { toast } from 'sonner';
import {
  ticketService,
  type StaffTicket,
  type TicketStatus,
  type TicketPriority,
} from '@/lib/services/ticket.service';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Caixa } from '@/construtor/caixa';

const statusColors: Record<TicketStatus, string> = {
  OPEN: 'bg-primary',
  IN_PROGRESS: 'bg-warning',
  AWAITING_CUSTOMER: 'bg-warning',
  RESOLVED: 'bg-success',
  CLOSED: 'bg-muted',
};

const statusLabels: Record<TicketStatus, string> = {
  OPEN: 'Aberto',
  IN_PROGRESS: 'Em Andamento',
  AWAITING_CUSTOMER: 'Aguardando Resposta',
  RESOLVED: 'Resolvido',
  CLOSED: 'Fechado',
};

const priorityLabels: Record<TicketPriority, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

export default function StaffTicketsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { store } = useStore();
  const [tickets, setTickets] = useState<StaffTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterAssigned, setFilterAssigned] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [canViewAll, setCanViewAll] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (store?.id && isAuthenticated) {
      loadTickets();
    }
  }, [store?.id, isAuthenticated, filterStatus, filterAssigned, page]);

  const loadTickets = async () => {
    if (!store?.id) return;
    try {
      setLoading(true);
      const data = await ticketService.staffListTickets(store.id, {
        status: filterStatus !== 'all' ? (filterStatus as TicketStatus) : undefined,
        assignedToMe: filterAssigned === 'mine' ? true : undefined,
        page,
        perPage: 25,
      });
      setTickets(data.tickets);
      setCanViewAll(data.canViewAll);
      setTotalPages(data.pagination.totalPages);
    } catch {
      toast.error('Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = tickets.filter(
    (t) =>
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAuthenticated) {
    return (
      <div>
        <PageHero
          title="Painel de Atendimento"
          breadcrumbs={[{ label: 'Suporte', href: '/suporte' }, { label: 'Atendimento' }]}
        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">Você precisa estar logado.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        title="Painel de Atendimento"
        breadcrumbs={[{ label: 'Suporte', href: '/suporte' }, { label: 'Atendimento' }]}
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Input
              placeholder="Buscar tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-64"
            />

            <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-44">
                <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="OPEN">Aberto</SelectItem>
                <SelectItem value="IN_PROGRESS">Em Andamento</SelectItem>
                <SelectItem value="AWAITING_CUSTOMER">Aguardando Resposta</SelectItem>
                <SelectItem value="RESOLVED">Resolvido</SelectItem>
                <SelectItem value="CLOSED">Fechado</SelectItem>
              </SelectContent>
            </Select>

            {canViewAll && (
              <Select value={filterAssigned} onValueChange={(v) => { setFilterAssigned(v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Atribuição" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="mine">Atribuídos a mim</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          <Button variant="outline" onClick={() => navigate('/suporte')}>
            Meus Tickets
          </Button>
        </div>

        <Caixa id="suporte.equipe.tickets" miolo>
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <p className="text-muted-foreground">Carregando tickets...</p>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <Inbox className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">
                  {searchQuery ? 'Nenhum ticket encontrado' : 'Nenhum ticket disponível'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border">
                    <TableHead className="text-muted-foreground">#ID</TableHead>
                    <TableHead className="text-muted-foreground">Título</TableHead>
                    <TableHead className="text-muted-foreground">Prioridade</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">Atribuição</TableHead>
                    <TableHead className="text-muted-foreground">Criado há</TableHead>
                    <TableHead className="text-muted-foreground text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <Caixa id="suporte.equipe.linha" repetida key={ticket.id}>
                    <TableRow key={ticket.id} className="table-row-interactive border-border">
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        #{ticket.id.slice(0, 8)}
                      </TableCell>
                      <TableCell className="font-medium text-foreground">
                        {ticket.title}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{priorityLabels[ticket.priority]}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[ticket.status]}>
                          {statusLabels[ticket.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {ticket.isClaimedByMe ? (
                          <Badge variant="outline" className="border-green-500 text-green-600">
                            Atribuído a mim
                          </Badge>
                        ) : ticket.isClaimedByOther ? (
                          <Badge variant="outline" className="text-muted-foreground">
                            Em atendimento
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-orange-400 text-orange-500">
                            Na fila
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDistanceToNow(new Date(ticket.createdAt), {
                            addSuffix: true,
                            locale: ptBR,
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => navigate(`/suporte/staff/ticket/${ticket.id}`)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    </Caixa>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        </Caixa>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {page} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
